import React, { FC, useState, useEffect, Dispatch, SetStateAction } from 'react'
// @ts-ignore
import s from '@components/common/Navbar/Navbar.module.scss'
// @ts-ignore
import { UserNav } from '@components/common/UserNav/UserNav'
import { useRouter } from 'next/router'
import cn from 'classnames'
import throttle from 'lodash.throttle'
import { MegaMenu } from '@components/alfakher/pylot-megamenu/src/MegaMenu'
import { useTranslation } from 'next-i18next'
// @ts-ignore
import LocationModal from '@ooka_default/components/common/LocationModal'
import { useAlfakherContext } from '../../alfakherContext/AlfakherContext'
import { usePreloadedData } from '@preload'
import { PromoBanner } from '@corratech/pylot-promobanner'
import { RichContent } from '@corratech/pylot-rich-content'
import { useIsMobile } from '@components/common/IsMobile/useIsMobile'
import Link from '@corratech/pylot-ui/src/Link'
import Logo from '@components/icons/Logo'

/**
 * Is used to query element by id and get navbar's height as an offset
 * when scrolling to the target element.
 */
export const NAVBAR_ID = 'navbar'
const SHOW_CURRENCY = false

declare global {
    interface Window {
        HubSpotConversations?: {
            widget: {
                open: () => void
                load: () => void
                close: () => void
                status: () => { loaded: boolean }
            }
        }
    }
}

type cmsBlocks = {
    identifier: string
    title: string
    content: string
}

type CmsBlocksResponse = {
    data: {
        cmsBlocks: {
            items: cmsBlocks[] | [null]
        }
    }
}

const Navbar: FC<{
    displaySearch: boolean
    setIsPromoCookieCleared?: Dispatch<SetStateAction<boolean>>
    isPromoCookieCleared?: boolean
}> = ({ setIsPromoCookieCleared, isPromoCookieCleared }) => {
    const [hasScrolled, setHasScrolled] = useState(false)
    const [heroVisible, setHeroVisible] = useState(false)
    const [scrollDirection, setScrollDirection] = useState('')
    const { t } = useTranslation('common')
    const router = useRouter()
    const { asPath, pathname } = router
    const { showLocationModal, setShowLocationModal } = useAlfakherContext()
    const initialData = (usePreloadedData(
        'header'
    ) as unknown) as CmsBlocksResponse
    const isMobile = useIsMobile()
    const items = initialData?.data?.cmsBlocks?.items ?? []

    const transparentHeaderUrls = [
        '/c/ooka-device',
        '/c/explore-flavors',
        '/c/discover-ooka'
    ]
    const isCheckout = pathname.includes('/checkout')

    useEffect(() => {
        const layoutClass = document.getElementById('layout-main')
        if (layoutClass) {
            if (asPath === '/') {
                layoutClass.classList.add('ookaHome')
                layoutClass.classList.remove('ookaCategory')
            } else if (transparentHeaderUrls.includes(asPath)) {
                layoutClass.classList.add('ookaCategory')
                layoutClass.classList.remove('ookaHome')
            } else {
                layoutClass.classList.remove('ookaHome')
                layoutClass.classList.remove('ookaCategory')
            }
        }
    }, [asPath])

    useEffect(() => {
        let lastScrollY = window.scrollY
        const HERO_END_MARKER_SELECTOR = 'video_div'
        const NAVBAR_HEIGHT_OFFSET = 130
        const handleScroll = throttle(() => {
            // function to run on scroll

            const scrollY = window?.scrollY
            const direction = scrollY > lastScrollY ? 'down' : 'up'
            if (direction !== scrollDirection) {
                setScrollDirection(direction)
            }
            lastScrollY = scrollY > 0 ? scrollY : 0
            const targetEl = document.getElementsByClassName(
                HERO_END_MARKER_SELECTOR
            )[0]
            if (targetEl) {
                const rect = targetEl.getBoundingClientRect()
                const isVisible =
                    rect.bottom - NAVBAR_HEIGHT_OFFSET > 0 &&
                    rect.bottom - NAVBAR_HEIGHT_OFFSET <
                        document.documentElement.clientHeight
                setHeroVisible(!isVisible)
            }
            const offset = 0
            const { scrollTop } = document.documentElement
            const scrolled = scrollTop > offset
            if (!targetEl) {
                setHeroVisible(scrolled)
            }
            setHasScrolled(scrolled)
        }, 200)
        if (!items[0] && setIsPromoCookieCleared) setIsPromoCookieCleared(true)

        document.addEventListener('scroll', handleScroll)
        return () => {
            document.removeEventListener('scroll', handleScroll)
        }
    }, [])

    const handleChatOpen = () => {
        if (typeof window !== 'undefined' && window.HubSpotConversations) {
            window.HubSpotConversations.widget.open()
        }
    }
    const renderLogoAndNavigation = () => (
        <Link href="/">
            <span className={s.logo} aria-label={t('Logo')}>
                <Logo />
                <span className="sr-only">{t('Logo')}</span>
            </span>
        </Link>
    )

    const isCustomkit = router.query.option === 'customize'

    if (isCheckout) {
        return (
            <div
                className={cn(s.root, 'top-0', 'header-parent', {
                    blackHeaderOnScroll: heroVisible
                })}
            >
                <div className={s['header-main']}>
                    <div
                        className={`${s['top-header-section']} ${s['custom-top-header-section']} flex`}
                    >
                        <div
                            className={cn(
                                s['top-header-logo-custom'],
                                'mx-auto'
                            )}
                        >
                            {renderLogoAndNavigation()}
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    return (
        <>
            <LocationModal
                setShowLocationModal={setShowLocationModal}
                showLocationModal={showLocationModal}
            />
            <div
                className={cn(
                    s.root,
                    s['fixed-search'],
                    'header-parent',
                    {
                        blackHeaderOnScroll: heroVisible
                    },
                    [
                        isMobile && scrollDirection === 'down'
                            ? s['HeaderOnScroll']
                            : s['scrollHeaderUp']
                    ]
                )}
            >
                <div>
                    {isCustomkit
                        ? ' '
                        : items?.[0] && (
                              <PromoBanner
                                  className={s['promo-banner z-99']}
                                  cookieName="promobanner"
                                  setIsPromoCookieCleared={
                                      setIsPromoCookieCleared
                                  }
                                  isPromoCookieCleared={isPromoCookieCleared}
                                  closeButton={
                                      <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="5"
                                          id="promobanner_close"
                                          height="5"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                      >
                                          <path
                                              opacity="0.15"
                                              d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                              fill="#000000"
                                          />
                                          <path
                                              d="M16 8L8 16M8.00001 8L16 16M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                                              stroke="#000000"
                                              strokeWidth="1.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                          />
                                      </svg>
                                  }
                              >
                                  <RichContent html={items[0]?.content} />
                              </PromoBanner>
                          )}
                </div>
                <div className={s['header-main']}>
                    <div
                        className={`${s['top-header-section']} ${
                            isCustomkit ? s['custom-top-header-section'] : ''
                        }`}
                    >
                        {isCustomkit ? (
                            <>
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={() => {
                                        if (
                                            typeof window != 'undefined' &&
                                            window.history.back
                                        ) {
                                            window.history.back()
                                        }
                                    }}
                                    onClick={() => {
                                        if (
                                            typeof window != 'undefined' &&
                                            window.history.back
                                        ) {
                                            window.history.back()
                                        }
                                    }}
                                    className={s['back-arrow']}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        width="24"
                                        height="24"
                                        fill="none"
                                    >
                                        <path
                                            d="M15 18l-6-6 6-6"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            fill="none"
                                        />
                                    </svg>
                                </span>
                                <div className={s['top-header-logo-custom']}>
                                    {renderLogoAndNavigation()}
                                </div>
                            </>
                        ) : (
                            <MegaMenu
                                className={cn(
                                    'megamenu-cms',
                                    !hasScrolled && 'megamenu-not-sticky'
                                )}
                                showCurrency={SHOW_CURRENCY}
                                handleChatOpen={handleChatOpen}
                            />
                        )}

                        <div
                            className={`${s['top-header-right-section']} ${
                                isCustomkit ? s['custom-kit-right-section'] : ''
                            }`}
                        >
                            <UserNav showCurrency={SHOW_CURRENCY} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Navbar
